import { SseDecoder, type AgentResponse, type BillingResponse, type Citation, type ConversationDetailResponse, type ConversationResponse, type DocumentResponse, type MeResponse, type PolicyResponse } from "@bubble/core";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type PolicyDraft = {
  agentName: string;
  tone: string;
  allowedTopics: string[];
  forbiddenTopics: string[];
  guardrails: string;
  fallbackBehavior: "message" | "collect_contact" | "handoff_email";
  fallbackMessage: string;
  handoffEmail: string | null;
  maxAnswerLength: number;
};

export function apiUrl(): string {
  return (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
}

export function configError(): string | null {
  if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
    return "Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to apps/dashboard/.env.local.";
  }
  if (!apiUrl()) return "Add VITE_API_URL to apps/dashboard/.env.local.";
  return null;
}

export async function api<T>(path: string, token: string, init: { method?: string; json?: unknown; form?: FormData } = {}): Promise<T> {
  const headers = new Headers();
  headers.set("authorization", `Bearer ${token}`);
  let body: BodyInit | undefined;
  if (init.form) body = init.form;
  else if (init.json !== undefined) {
    headers.set("content-type", "application/json");
    body = JSON.stringify(init.json);
  }
  let response: Response;
  try {
    response = await fetch(`${apiUrl()}${path}`, { method: init.method ?? "GET", headers, body });
  } catch {
    throw new ApiError(0, "network", "Could not reach Bubble. Check your connection and try again.");
  }
  if (!response.ok) throw await errorFrom(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function streamApi(
  path: string,
  token: string,
  json: unknown,
  onEvent: (event: string, data: Record<string, unknown>) => void,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${apiUrl()}${path}`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify(json),
    });
  } catch {
    throw new ApiError(0, "network", "Could not reach Bubble. Check your connection and try again.");
  }
  if (!response.ok || !response.body) throw await errorFrom(response);
  const decoder = new SseDecoder();
  const reader = response.body.getReader();
  const utf8 = new TextDecoder();
  while (true) {
    const step = await reader.read();
    if (step.done) break;
    emit(decoder.push(utf8.decode(step.value, { stream: true })), onEvent);
  }
  emit(decoder.push("\n\n"), onEvent);
}

function emit(events: { event: string; data: unknown }[], onEvent: (event: string, data: Record<string, unknown>) => void): void {
  for (const item of events) {
    const data = item.data && typeof item.data === "object" ? (item.data as Record<string, unknown>) : {};
    onEvent(item.event, data);
  }
}

async function errorFrom(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string } };
    return new ApiError(response.status, body.error?.code ?? "request_failed", body.error?.message ?? "The request failed.");
  } catch {
    return new ApiError(response.status, "request_failed", "The request failed.");
  }
}

export function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export type { AgentResponse, BillingResponse, Citation, ConversationDetailResponse, ConversationResponse, DocumentResponse, MeResponse, PolicyResponse };
